import React, {
  FC, useCallback, useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { ArticleListContext } from '../articleListContext';
import { BudgetInvoice } from '../budgetInvoice';
import { articleFlowerCollection } from './articleFlowerCollection';
import { BudgetInvoiceModalComponent } from './types';
import Modal from 'components/base/Modal';

import './index.scss';
import { ArticleTrackingDtoStatus } from 'config/apiFunus/generated/data-contracts';
import { BudgetQuestionCodes } from 'modules/budget/data/budgetQuestionCodes';
import { useBudget } from 'modules/budget/hooks/useBudget';

export const BudgetInvoiceModal: FC<BudgetInvoiceModalComponent> = ({
  onCancel, show, budgetId, onSuccess, erpId,
}) => {
  const { t } = useTranslation();
  const {
    InvoiceBudget,
    invoiceArticlesLoading,
    invoiceArticles,
    invoiceBudgetLoading,
  } = useBudget();
  const [acceptEnabled, setAcceptEnabled] = useState<boolean>(false);

  const [infoProvidedFlowers, setInfoProvidedFlowers] = useState<articleFlowerCollection>([]);
  const pendingFlowersQuantity = useMemo(
    () => invoiceArticles
      .reduce((prev, current) => prev + current.recordArticles
        .filter((a) => a.status === ArticleTrackingDtoStatus.DRAFT)?.length, 0),
    [invoiceArticles],
  );

  useEffect(
    () => {
      const notFlowersArticlesWithPendingData = Boolean(invoiceArticles
        .find(
          (article) => article.questionCode !== BudgetQuestionCodes.Q14
          && (!article.client || !article.provider),
        ));
      const isAllFlowersInfoProvided = pendingFlowersQuantity === infoProvidedFlowers.length;

      setAcceptEnabled(!invoiceArticlesLoading
        && isAllFlowersInfoProvided
        && !invoiceBudgetLoading
        && invoiceArticles.length !== 0
        && !notFlowersArticlesWithPendingData);
    },
    [infoProvidedFlowers.length,
      pendingFlowersQuantity, invoiceArticles, invoiceArticlesLoading, invoiceBudgetLoading],
  );

  useEffect(() => {
    if (!show) {
      setAcceptEnabled(false);
    }
  }, [show]);

  const handleUpdate = useCallback((id, article) => {
    setInfoProvidedFlowers(
      [
        ...infoProvidedFlowers.filter((a) => a.code !== id),
        {
          article,
          code: id,
        },
      ],
    );
  }, [infoProvidedFlowers]);

  return (
    <ArticleListContext.Provider
      value={{
        infoProvidedFlowers,
        updateItem: handleUpdate,
      }}
    >
      <Modal
        buttons={[
          {
            onClick: () => {
              setInfoProvidedFlowers([]);
              onCancel();
            },
            text: t('common.close'),
          },
          {
            disabled: !acceptEnabled,
            onClick: () => {
              InvoiceBudget(budgetId, infoProvidedFlowers)
                .then((response) => {
                  onSuccess();
                  return response;
                })
                .catch(() => false);
            },
            text: t('common.accept'),
          },
        ]}
        className="f-budget-invoice-modal"
        closeOnFocusLost={false}
        show={show}
        showCancel={false}
        showXButton={false}
        title={t('budget.actions.invoice')}
        onHide={onCancel}
      >
        {show && (
        <BudgetInvoice
          budgetId={budgetId}
          erpId={erpId}
        />
        )}
      </Modal>
    </ArticleListContext.Provider>
  );
};
